import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { formatDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { F } from "@/pages/Employees";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const weekRange = (offset = 0) => {
  const d = new Date(); d.setDate(d.getDate() - d.getDay() + offset * 7);
  const e = new Date(d); e.setDate(d.getDate() + 6);
  return [iso(d), iso(e)];
};

export default function ReportOvertimeHigh() {
  const [[start, end], setRange] = useState(weekRange());
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/reports/overtime-high?start=${start}&end=${end}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [start, end]);
  return (
    <div className="space-y-5">
      <PageHeader title="Laporan Lembur Tinggi" subtitle="Karyawan dengan lembur melewati batas wajar per hari, beserta mandor & penginputnya" />
      <Card className="p-4 flex flex-wrap items-end gap-3">
        <div className="w-44"><F label="Dari"><Input type="date" value={start} onChange={(e) => setRange([e.target.value, end])} data-testid="oth-start" /></F></div>
        <div className="w-44"><F label="Sampai"><Input type="date" value={end} onChange={(e) => setRange([start, e.target.value])} data-testid="oth-end" /></F></div>
        <Button variant="outline" onClick={() => setRange(weekRange())} data-testid="oth-this-week">Minggu Ini</Button>
        <Button variant="outline" onClick={() => setRange(weekRange(-1))} data-testid="oth-last-week">Minggu Lalu</Button>
        {data && <div className="ml-auto rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800" data-testid="oth-batas">
          <AlertTriangle className="inline h-4 w-4 mr-1" /> Batas wajar: <b>{data.batas} jam/hari</b> · {data.per_karyawan.length} karyawan · {data.rows.length} kejadian
        </div>}
      </Card>
      {data && <Summary list={data.per_karyawan} />}
      {data && <Detail rows={data.rows} />}
    </div>
  );
}

const TH = ({ children, right }) => <th className={`px-3 py-2.5 ${right ? "text-right" : "text-left"}`}>{children}</th>;

const Summary = ({ list }) => (
  <Card className="p-0 overflow-x-auto scroll-thin">
    <div className="px-4 py-3 font-heading font-bold text-slate-800">Ringkasan per Karyawan</div>
    <table className="w-full text-sm" data-testid="oth-summary">
      <thead><tr className="bg-blue-900 text-white text-xs uppercase"><TH>Nama</TH><TH>Mandor</TH><TH right>Hari Lewat Batas</TH><TH right>Total Lembur</TH><TH>Diinput Oleh</TH></tr></thead>
      <tbody>
        {list.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400">Tidak ada lembur yang melewati batas.</td></tr>}
        {list.map((p, i) => (
          <tr key={p.employee_id} className="border-b border-slate-100" data-testid={`oth-sum-${i}`}>
            <td className="px-3 py-2 font-semibold">{p.employee_nama} <span className="text-xs text-slate-400">{p.kode_pegawai}</span></td>
            <td className="px-3 py-2">{p.mandor_nama}</td>
            <td className="px-3 py-2 text-right font-mono">{p.hari}x</td>
            <td className="px-3 py-2 text-right font-mono font-bold text-rose-600">{p.jam_lembur} jam</td>
            <td className="px-3 py-2 text-xs">{p.input_by.join(", ")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </Card>
);

const Detail = ({ rows }) => (
  <Card className="p-0 overflow-x-auto scroll-thin">
    <div className="px-4 py-3 font-heading font-bold text-slate-800">Rincian Harian</div>
    <table className="w-full text-sm" data-testid="oth-detail">
      <thead><tr className="bg-blue-900 text-white text-xs uppercase"><TH>Tanggal</TH><TH>Nama</TH><TH>Proyek</TH><TH>Jam</TH><TH right>Lembur</TH><TH right>Lebih</TH><TH>Mandor</TH><TH>Diinput Oleh</TH></tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400">—</td></tr>}
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-slate-100" data-testid={`oth-row-${i}`}>
            <td className="px-3 py-2 font-mono whitespace-nowrap">{formatDate(r.tanggal)}</td>
            <td className="px-3 py-2">{r.employee_nama}</td><td className="px-3 py-2">{r.project_nama}</td>
            <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.jam_masuk}–{r.jam_pulang}</td>
            <td className="px-3 py-2 text-right font-mono font-bold">{r.jam_lembur} j</td>
            <td className="px-3 py-2 text-right font-mono text-rose-600">+{r.lebih} j</td>
            <td className="px-3 py-2">{r.mandor_nama}</td><td className="px-3 py-2 text-xs">{r.input_by}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </Card>
);
